// Fallback for using MaterialIcons on Android and web.

import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { SymbolViewProps, SymbolWeight } from 'expo-symbols';
import { ComponentProps } from 'react';
import { OpaqueColorValue, type StyleProp, type TextStyle } from 'react-native';

type IconMapping = Partial<
  Record<SymbolViewProps['name'] | (string & {}), ComponentProps<typeof MaterialIcons>['name']>
>;
export type IconSymbolName = keyof typeof MAPPING;

/**
 * Add your SF Symbols to Material Icons mappings here.
 * - see Material Icons in the [Icons Directory](https://icons.expo.fyi).
 * - see SF Symbols in the [SF Symbols](https://developer.apple.com/sf-symbols/) app.
 */
const MAPPING = {
  'house.fill': 'home',
  'paperplane.fill': 'send',
  'chevron.left.forwardslash.chevron.right': 'code',
  'chevron.right': 'chevron-right',
  'chevron.down': 'expand-more',
  'doc.text': 'description',
  'chart.bar': 'bar-chart',
  checkmark: 'check',
  xmark: 'close',
  'exclamationmark.triangle.fill': 'warning',
  'circle.badge.checkmark.fill': 'check-circle',
  pencil: 'edit',
  'arrow.clockwise': 'refresh',
  'calculator.fill': 'calculate',
  'shield.fill': 'verified-user',
  'bolt.fill': 'bolt',
  construction: 'construction',
  'mail.fill': 'email',
  calendar: 'calendar-today',
  'file-open': 'file-open',
  'plus': 'add',
  'info.circle': 'info',
  'exclamationmark.error': 'error',
  'trash': 'delete',
  'doc.on.clipboard.fill': 'file-copy',
  'bar-chart': 'bar-chart',
  'pie-chart': 'pie-chart'
} satisfies IconMapping;

/**
 * An icon component that uses native SF Symbols on iOS, and Material Icons on Android and web.
 * This ensures a consistent look across platforms, and optimal resource usage.
 * Icon `name`s are based on SF Symbols and require manual mapping to Material Icons.
 */
export function IconSymbol({
  name,
  size = 24,
  color,
  style,
}: {
  name: IconSymbolName;
  size?: number;
  color: string | OpaqueColorValue;
  style?: StyleProp<TextStyle>;
  weight?: SymbolWeight;
}) {
  return (
    <MaterialIcons
      color={color}
      size={size}
      name={MAPPING[name] as ComponentProps<typeof MaterialIcons>['name']}
      style={style}
    />
  );
}
